import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, LogIn } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

const DEMO_ACCOUNTS = [
  { label: 'Admin', email: 'admin@catesroofing.com', role: 'admin', badge: 'bg-red-100 text-red-700' },
  { label: 'Customer', email: 'sarah@example.com', role: 'customer', badge: 'bg-blue-100 text-blue-700' },
  { label: 'Contractor', email: 'marcus@greenroofing.com', role: 'contractor', badge: 'bg-green-100 text-green-700' },
];

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAppStore();
  const navigate = useNavigate();

  const handleLogin = (e: React.FormEvent, overrideEmail?: string) => {
    e.preventDefault();
    const target = overrideEmail || email;
    const ok = login(target);
    if (ok) {
      const { user } = useAppStore.getState();
      const dest = user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'contractor' ? '/contractor/dashboard' : '/portal/dashboard';
      navigate(dest);
    } else {
      setError('Invalid credentials. Try a demo account below.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg-secondary)' }}>
      {/* Left Panel */}
      <div style={{ flex: '0 0 480px', background: 'linear-gradient(135deg, #020617, #0f172a, #1a0500)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3rem', position: 'relative', overflow: 'hidden' }} className="login-panel">
        <div style={{ position: 'absolute', top: '15%', right: '-5%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgb(249 115 22 / 0.08), transparent 70%)', pointerEvents: 'none' }} />
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none', marginBottom: '4rem' }}>
          <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: 'linear-gradient(135deg, #f97316, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="white" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: 800, fontSize: '1rem' }}>Cates Roofing</div>
            <div style={{ color: '#f97316', fontSize: '0.65rem', fontWeight: 500, letterSpacing: '0.05em' }}>& CONTRACTING</div>
          </div>
        </Link>
        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.2, marginBottom: '1.25rem' }}>
          Welcome Back to Your Portal
        </h1>
        <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '2.5rem' }}>
          Access your projects, quotes, appointments, and invoices — all in one place.
        </p>
        {['Track your project in real-time', 'Review and approve quotes online', 'Manage appointments & invoices', 'Upload documents securely'].map(item => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />
            <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Right Panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <h2 style={{ fontWeight: 800, fontSize: '1.75rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Sign In</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', fontSize: '0.9rem' }}>
            Don't have an account? <Link to="/register" style={{ color: '#f97316', fontWeight: 600 }}>Register here</Link>
          </p>

          {/* Demo Accounts */}
          <div style={{ background: 'var(--bg-secondary)', borderRadius: '0.875rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid var(--border-color)' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Demo Accounts</p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {DEMO_ACCOUNTS.map(acc => (
                <button key={acc.role} onClick={e => { setEmail(acc.email); handleLogin(e, acc.email); }} style={{
                  padding: '0.375rem 0.875rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'opacity 0.2s',
                  background: acc.role === 'admin' ? '#fee2e2' : acc.role === 'customer' ? '#dbeafe' : '#dcfce7',
                  color: acc.role === 'admin' ? '#991b1b' : acc.role === 'customer' ? '#1e40af' : '#166534',
                }}>
                  {acc.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleLogin}>
            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '0.625rem', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label className="label">Email Address</label>
              <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label">Password</label>
              <div style={{ position: 'relative' }}>
                <input className="input-field" type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" style={{ paddingRight: '2.75rem' }} />
                <button type="button" onClick={() => setShow(!show)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: '0.375rem' }}>
                <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: '#f97316' }}>Forgot password?</Link>
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
              <LogIn size={16} /> Sign In
            </button>
          </form>

          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '2rem' }}>
            Need emergency help? <a href="tel:5558472663" style={{ color: '#f97316', fontWeight: 600 }}>Call 24/7</a>
          </p>
        </div>
      </div>
      <style>{`@media(max-width:768px){.login-panel{display:none!important}}`}</style>
    </div>
  );
};

export default LoginPage;
